import clsx from 'clsx';

import { LoadingSpinner } from '../../../components/LoadingSpinner';
import type { DocumentJobStatus, DocumentProcessingStage } from '../api';
import { describeClassificationReason } from './classificationMeta';

interface StatusBannerProps {
  status: DocumentJobStatus;
  triggerReason?: string;
  /** Live pipeline stage from the status endpoint; only meaningful while queued/processing. */
  currentStage?: DocumentProcessingStage | string | null;
  /**
   * When true, the job was auto-classified: the step counter includes the extra «Clasificando» beat
   * («Paso N/7»). Manual jobs keep the 6-step rail.
   */
  classified?: boolean;
}

/**
 * Ordered, user-visible pipeline stages used to render "Paso N/6". The order mirrors the backend
 * `DocumentProcessingStageMapper` progression; labels are Spanish copy for the reviewer.
 */
const STAGE_STEPS: ReadonlyArray<{ stage: DocumentProcessingStage; label: string }> = [
  { stage: 'queued', label: 'En cola' },
  { stage: 'preparing', label: 'Preparando documento' },
  { stage: 'ocr', label: 'Leyendo documento (OCR)' },
  { stage: 'extracting', label: 'Extrayendo campos (IA)' },
  { stage: 'validating', label: 'Validando datos' },
  { stage: 'finalizing', label: 'Finalizando' },
];

/** The 7-step rail for an auto-classified job: «Clasificando» inserted after «En cola». */
const STAGE_STEPS_CLASSIFIED: ReadonlyArray<{ stage: DocumentProcessingStage; label: string }> = [
  STAGE_STEPS[0]!,
  { stage: 'classifying', label: 'Clasificando el documento' },
  ...STAGE_STEPS.slice(1),
];

const COPY: Record<DocumentJobStatus, { title: string; tone: string }> = {
  queued: {
    title: 'En cola — esperando a que el motor lo procese.',
    tone: 'bg-slate-100 text-slate-700 border-slate-300',
  },
  processing: {
    title: 'Procesando documento…',
    tone: 'bg-slate-100 text-slate-700 border-slate-300',
  },
  approved: {
    title: 'Documento aprobado automáticamente.',
    tone: 'bg-emerald-50 text-emerald-800 border-emerald-300',
  },
  review_required: {
    title: 'Requiere revisión',
    tone: 'bg-amber-50 text-amber-800 border-amber-300',
  },
  rejected: {
    title: 'Documento rechazado',
    tone: 'bg-rose-50 text-rose-800 border-rose-300',
  },
  failed: {
    title: 'Fallo durante el procesamiento.',
    tone: 'bg-slate-100 text-slate-700 border-slate-400',
  },
  split: {
    title: 'Documento dividido en varios sub-documentos.',
    tone: 'bg-indigo-50 text-indigo-800 border-indigo-300',
  },
};

/**
 * Top-of-panel banner reflecting the document lifecycle status.
 *
 * Color transitions are animated (`transition-colors duration-500`) so that
 * when a re-validation flips `review_required → approved` the change is visible
 * without being jarring.
 */
export function StatusBanner({ status, triggerReason, currentStage, classified = false }: StatusBannerProps): JSX.Element {
  const { title, tone } = COPY[status];
  const showSpinner = status === 'queued' || status === 'processing';

  const steps = classified ? STAGE_STEPS_CLASSIFIED : STAGE_STEPS;
  // Step progress is only meaningful while in flight; terminal banners keep their plain copy.
  const stepIndex =
    showSpinner && currentStage !== undefined && currentStage !== null
      ? steps.findIndex((s) => s.stage === currentStage)
      : -1;
  const step = stepIndex >= 0 ? steps[stepIndex] : undefined;

  // A classification-driven ReviewRequired reason (confidence cap) gets friendly copy; anything else
  // renders the raw reason as before.
  const reasonCopy = describeClassificationReason(triggerReason) ?? triggerReason;

  return (
    <div
      role="status"
      className={clsx(
        'flex items-start gap-3 rounded-md border p-3 text-sm transition-colors duration-500',
        tone,
      )}
    >
      {showSpinner ? <LoadingSpinner size="sm" /> : <StatusIcon status={status} />}
      <div className="flex-1">
        <div className="font-semibold">
          {step !== undefined
            ? `Paso ${stepIndex + 1}/${steps.length} — ${step.label}`
            : title}
        </div>
        {reasonCopy !== undefined && reasonCopy.length > 0 && status !== 'approved' ? (
          <div className="mt-0.5 text-sm opacity-90">{reasonCopy}</div>
        ) : null}
        {status === 'rejected' ? (
          <div className="mt-1 text-xs opacity-80">
            Verifica que el tipo de documento es el correcto antes de reintentar.
          </div>
        ) : null}
      </div>
    </div>
  );
}

function StatusIcon({ status }: { status: DocumentJobStatus }): JSX.Element {
  if (status === 'approved') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mt-0.5 h-5 w-5">
        <path d="M5 12l4 4 10-10" />
      </svg>
    );
  }
  if (status === 'review_required') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mt-0.5 h-5 w-5">
        <path d="M12 9v4" />
        <path d="M12 17h.01" />
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      </svg>
    );
  }
  if (status === 'rejected' || status === 'failed') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mt-0.5 h-5 w-5">
        <circle cx="12" cy="12" r="10" />
        <path d="M15 9l-6 6" />
        <path d="M9 9l6 6" />
      </svg>
    );
  }
  if (status === 'split') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mt-0.5 h-5 w-5">
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mt-0.5 h-5 w-5">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  );
}
